import React, { useState } from "react";
import { Button, Tooltip, notification } from "antd";
import { FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import { useExportJobs } from "./useExportJobs";
import { exportNodeToPdf } from "./exportPdf";
import "./export.scss";

/* The two exports a single site-visit card offers, sitting at the top of its
   preview.

   They are deliberately not the same kind of thing, because the card is not the
   same kind of thing in each:

   - Excel queues the ordinary server export for exactly this one card, so the
     workbook has the same sheets and the same columns as the one you would get
     by ticking that row in the table. It arrives as a notification with a
     download button, like every other export.
   - PDF copies the preview itself — the card as it is on screen, sections,
     pills, colours and all. That is the point of it: people take this to a
     meeting, and a workbook with one row in it is not what they meant. */

const SiteVisitExportActions = ({
  siteVisitCardId,
  identifier,
  targetRef,
  disabled = false,
}) => {
  const { submit, submitting } = useExportJobs({ scope: "user", open: false });
  const [buildingPdf, setBuildingPdf] = useState(false);

  const blocked = disabled || !siteVisitCardId;

  const handleExcel = () => {
    if (blocked || submitting) return;
    submit({ siteVisitIds: [siteVisitCardId] });
  };

  const handlePdf = async () => {
    if (blocked || buildingPdf) return;
    setBuildingPdf(true);

    const result = await exportNodeToPdf({
      node: targetRef?.current,
      fileName: identifier ? `site-visit-${identifier}` : "site-visit",
    });

    setBuildingPdf(false);

    if (result?.ok) {
      notification.success({
        key: `preview-pdf-${siteVisitCardId}`,
        message: "PDF downloaded",
        description: result.fileName,
        duration: 4,
      });
    } else {
      notification.error({
        message: "PDF failed",
        description: result?.error || "We couldn't build the PDF.",
        duration: 6,
      });
    }
  };

  return (
    /* data-pdf-hide: the bar is the thing that makes the PDF, so it has no
       business being in it. exportNodeToPdf strips it from the clone. */
    <div className="siteVisitExportActions" data-pdf-hide>
      <Tooltip title="Queue the Excel export for this site visit">
        <Button
          className="siteVisitExportBtn"
          icon={<FileExcelOutlined />}
          loading={submitting}
          disabled={blocked}
          onClick={handleExcel}
        >
          Export Excel
        </Button>
      </Tooltip>
      <Tooltip title="Save this preview exactly as it looks">
        <Button
          className="siteVisitExportBtn is-pdf"
          icon={<FilePdfOutlined />}
          loading={buildingPdf}
          disabled={blocked}
          onClick={handlePdf}
        >
          Export PDF
        </Button>
      </Tooltip>
    </div>
  );
};

export default SiteVisitExportActions;
